import { Capacitor } from "@capacitor/core";

import { applyNativeRules } from "../native/rules";
import { orchestrateNativeWebFilter, type WebFilterOrchestrationResult } from "../native/webFilter";
import { computeBreakSuppressedUntil } from "./enforcementBreak";
import { getEnforcingRules, getNativeScheduledRules, isRuleStoppableAt } from "./ruleSchedule";
import {
  computeNextActivationInTimeZone,
  getDeviceLocalScheduleParts,
  getDeviceTimeZone,
} from "./ruleScheduleTimeZone";
import type { Rule, RulesCapability, Target } from "./ruleTypes";

export { getDeviceTimeZone };

export type EnforcementSyncResult = WebFilterOrchestrationResult & {
  appBlocking: RulesCapability;
};

export type NativeAppBlockingPlatform = "ios" | "android" | "web";

/**
 * Which rules to persist in the native app-blocking store.
 *
 * iOS DeviceActivity must stay registered for every enabled recurring window,
 * even when none are in-window at `now`. Android's FGS currently receives only
 * the live enforcing set (unchanged).
 */
export function rulesForNativeAppBlocking(
  rules: Rule[],
  now: Date,
  platform: NativeAppBlockingPlatform | string = Capacitor.getPlatform(),
): Rule[] {
  if (platform === "ios") return getNativeScheduledRules(rules, now);
  return getEnforcingRules(rules, now);
}

/** Push the native app-blocking plan + align the web filter VPN. */
export async function syncEnforcementToNative(
  rules: Rule[],
  targets: Target[],
  now: Date = new Date(),
): Promise<EnforcementSyncResult> {
  const appRules = rulesForNativeAppBlocking(rules, now);
  const [appStatus, webResult] = await Promise.all([
    applyNativeRules(appRules, targets),
    orchestrateNativeWebFilter(rules, targets, now),
  ]);
  return { ...webResult, appBlocking: appStatus.appBlocking };
}

function scheduleClock(nowMs: number, timeZone: string) {
  const { dayOfWeek, minuteOfDay } = getDeviceLocalScheduleParts(nowMs, timeZone);
  return { dayOfWeek, minuteOfDay, nowMs };
}

/** Local projection of rules after a full swipe-to-unlock stop (before Convex subscription catches up). */
export function projectRulesAfterStop(rules: Rule[], nowMs: number, timeZone: string): Rule[] {
  const clock = scheduleClock(nowMs, timeZone);
  const result: Rule[] = [];
  for (const rule of rules) {
    if (!isRuleStoppableAt(rule, clock)) {
      result.push(rule);
      continue;
    }
    if (rule.source === "quick_session") continue;
    result.push({
      ...rule,
      suppressedUntil: computeNextActivationInTimeZone(rule, nowMs, timeZone),
      updatedAt: nowMs,
    });
  }
  return result;
}

/** Local projection after manually resuming one forced-suspended rule. */
export function projectRulesAfterResume(rules: Rule[], ruleId: string, nowMs: number): Rule[] {
  return rules.map((rule) => {
    if (rule._id !== ruleId) return rule;
    return { ...rule, suppressedUntil: undefined, updatedAt: nowMs };
  });
}

/** Local projection of rules after a temporary break pause. */
export function projectRulesAfterBreak(
  rules: Rule[],
  nowMs: number,
  timeZone: string,
  durationMinutes: number,
): Rule[] {
  const clock = scheduleClock(nowMs, timeZone);
  return rules.map((rule) => {
    if (!isRuleStoppableAt(rule, clock)) return rule;
    return {
      ...rule,
      suppressedUntil: computeBreakSuppressedUntil(rule.suppressedUntil, nowMs, durationMinutes),
      updatedAt: nowMs,
    };
  });
}
